
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Eye, EyeOff, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { debugLogger } from '@/utils/debugLogger';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [passwordStrength, setPasswordStrength] = useState<"weak" | "medium" | "strong" | null>(null);
  const [shakePassword, setShakePassword] = useState(false);

  // Password strength validation
  useEffect(() => {
    if (password) {
      const hasLowercase = /[a-z]/.test(password);
      const hasUppercase = /[A-Z]/.test(password);
      const hasNumber = /[0-9]/.test(password);
      const hasSpecial = /[^A-Za-z0-9]/.test(password);
      const isLongEnough = password.length >= 8;
      const strengthScore = [hasLowercase, hasUppercase, hasNumber, hasSpecial, isLongEnough].filter(Boolean).length;
      
      if (strengthScore <= 2) setPasswordStrength("weak");
      else if (strengthScore <= 4) setPasswordStrength("medium");
      else setPasswordStrength("strong");
    } else {
      setPasswordStrength(null);
    }
  }, [password]);

  useEffect(() => {
    if (shakePassword) {
      const timer = setTimeout(() => {
        setShakePassword(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [shakePassword]);

  useEffect(() => {
    const verifyResetToken = async () => {
      debugLogger.debug('Verifying reset token...');
      debugLogger.debug('Current URL:', window.location.href);
      debugLogger.debug('Search params:', Object.fromEntries(searchParams.entries()));
      
      try {
        const token = searchParams.get('token');
        debugLogger.debug('Token from URL:', token);

        if (!token) {
          debugLogger.debug('No token found in URL');
          setError('Link de recuperação inválido - token não encontrado');
          setCheckingSession(false);
          return;
        }

        debugLogger.debug('Attempting to verify OTP token...');
        
        // Usar verifyOtp para estabelecer uma sessão de recuperação
        const { data, error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: token,
          type: 'recovery'
        });

        debugLogger.debug('VerifyOtp response:', { data, error: verifyError });

        if (verifyError) {
          debugLogger.error('Error verifying OTP:', verifyError);
          
          let errorMsg = 'Link de recuperação inválido ou expirado';
          if (verifyError.message?.includes('expired')) {
            errorMsg = 'O link de recuperação expirou. Solicite um novo link.';
          } else if (verifyError.message?.includes('invalid')) {
            errorMsg = 'Link de recuperação inválido. Verifique se você clicou no link correto.';
          } else if (verifyError.message?.includes('used')) {
            errorMsg = 'Este link de recuperação já foi usado. Solicite um novo se necessário.';
          }
          
          setError(errorMsg);
          setCheckingSession(false);
          return;
        }

        // Verificar se agora temos uma sessão válida
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        debugLogger.debug('Session after verifyOtp:', { session, error: sessionError });

        if (session && session.user) {
          debugLogger.debug('Valid recovery session established');
          setIsValidSession(true);
        } else {
          debugLogger.debug('No valid session after token verification');
          setError('Erro ao estabelecer sessão de recuperação. Tente novamente.');
        }

      } catch (err) {
        debugLogger.error('Unexpected error during token verification:', err);
        setError('Erro inesperado ao verificar token de recuperação');
      } finally {
        setCheckingSession(false);
      }
    };

    verifyResetToken();
  }, [searchParams]);

  const isFormValid = () => {
    if (!password || !confirmPassword) {
      setError('Por favor, preencha todos os campos');
      return false;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return false;
    }

    if (password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres');
      return false;
    }

    if (passwordStrength === "weak") {
      setError('Sua senha é muito fraca. Use letras maiúsculas, minúsculas, números e caracteres especiais.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      setShakePassword(true);
      return;
    }

    setLoading(true);
    setError('');

    try {
      debugLogger.debug('Attempting to update password...');
      
      const { data, error } = await supabase.auth.updateUser({
        password: password
      });

      debugLogger.debug('Update user response:', { data, error });

      if (error) {
        throw error;
      }

      debugLogger.debug('Password updated successfully');
      setSuccess(true);
      
      toast({
        title: "Senha Redefinida!",
        description: "Sua senha foi alterada com sucesso.",
      });
      
      // Redirecionar após 3 segundos
      setTimeout(() => {
        navigate('/auth');
      }, 3000);

    } catch (err: any) {
      debugLogger.error('Error updating password:', err);
      let errorMsg = 'Erro ao redefinir senha';
      
      if (err.message?.includes('session_not_found')) {
        errorMsg = 'Sessão expirada. Solicite um novo link de recuperação.';
      } else if (err.message?.includes('same_password')) {
        errorMsg = 'A nova senha deve ser diferente da senha atual.';
      } else if (err.message?.includes('weak_password')) {
        errorMsg = 'A senha é muito fraca. Use pelo menos 8 caracteres.';
      } else if (
        err.message?.includes('Password is known to be weak') ||
        err.message?.includes('weak and easy to guess') ||
        err.message?.toLowerCase().includes('hibp') ||
        err.message?.toLowerCase().includes('pwned')
      ) {
        errorMsg = 'Esta senha foi exposta em vazamentos de dados conhecidos. Por segurança, escolha uma senha diferente.';
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
      setShakePassword(true);
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-black/40 border-white/10">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-luxury-red mx-auto mb-4" />
            <p className="text-white/70">Verificando token de recuperação...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isValidSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-black/40 border-white/10">
          <CardContent className="p-8 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Link Inválido</h3>
            <p className="text-white/70 mb-4">
              {error || 'O link de recuperação é inválido ou expirou.'}
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => navigate('/auth')}
                className="w-full bg-luxury-red hover:bg-luxury-red/90"
              >
                Voltar ao Login
              </Button>
              <p className="text-xs text-white/50">
                Solicite um novo link de recuperação se necessário
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-black/40 border-white/10">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Senha Redefinida!</h2>
            <p className="text-white/70 mb-4">
              Sua senha foi alterada com sucesso. Você será redirecionado em instantes.
            </p>
            <div className="flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-luxury-red mr-2" />
              <span className="text-white/60">Redirecionando...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-black to-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-black/40 border-white/10">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-luxury-red to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-xl">LR</span>
          </div>
          <CardTitle className="text-2xl font-bold text-white">
            Redefinir Senha
          </CardTitle>
          <p className="text-white/70">
            Digite sua nova senha abaixo
          </p>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">Nova Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite sua nova senha"
                  className={cn(
                    "bg-white/5 border-white/10 text-white placeholder:text-white/50 pr-10",
                    shakePassword && "animate-[shake_0.5s_ease-in-out]"
                  )}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {passwordStrength && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-white/70">
                      Força da senha: 
                      <span className={passwordStrength === "weak" ? "text-red-500 ml-1" : passwordStrength === "medium" ? "text-yellow-500 ml-1" : "text-green-500 ml-1"}>
                        {passwordStrength === "weak" && "Fraca"}
                        {passwordStrength === "medium" && "Média"}
                        {passwordStrength === "strong" && "Forte"}
                      </span>
                    </span>
                  </div>
                  <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
                    <div className={`h-full transition-all duration-300 ${passwordStrength === "weak" ? "bg-red-500 w-1/3" : passwordStrength === "medium" ? "bg-yellow-500 w-2/3" : "bg-green-500 w-full"}`} />
                  </div>
                  {passwordStrength === "weak" && (
                    <p className="text-xs text-red-400 mt-1">
                      Adicione números, símbolos e letras maiúsculas para aumentar a segurança
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white">Confirmar Senha</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirme sua nova senha"
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/50 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <div className="flex items-center gap-2 text-red-400 text-xs">
                  <AlertCircle className="h-3 w-3" />
                  <span>As senhas não coincidem</span>
                </div>
              )}
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm p-3 bg-red-500/10 rounded-md">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-luxury-red hover:bg-luxury-red/90"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Redefinindo...
                </>
              ) : (
                'Redefinir Senha'
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/auth')}
              className="w-full text-white/70 hover:text-white hover:bg-white/10"
            >
              Voltar ao Login
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
